import { useState, useEffect } from 'react'
import { PlaneLanding, Plane, PlaneTakeoff, Building2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { TodayBoardCounters } from './useTodayBoardData'

interface CompactHeaderProps {
  selectedDate: Date
  counters: TodayBoardCounters
}

function isToday(date: Date): boolean {
  const now = new Date()
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

function formatLiveClock(now: Date): string {
  const weekday = now.toLocaleDateString('en-US', { weekday: 'short' })
  const month = now.toLocaleDateString('en-US', { month: 'short' })
  const day = now.getDate()
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  return `${weekday}, ${month} ${day} · ${hours}:${minutes}`
}

function formatDateOnly(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

// ── Counter pill sub-component ────────────────────────

interface CounterPillProps {
  icon: React.ComponentType<{ className?: string }>
  value: number | string
  tooltip: string
  colorClasses: string
  iconColor: string
}

function CounterPill({ icon: Icon, value, tooltip, colorClasses, iconColor }: CounterPillProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold cursor-default ${colorClasses}`}>
          <Icon className={`h-3.5 w-3.5 ${iconColor}`} />
          <span>{value}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">{tooltip}</TooltipContent>
    </Tooltip>
  )
}

// ── Main component ────────────────────────────────────

export function CompactHeader({ selectedDate, counters }: CompactHeaderProps) {
  const today = isToday(selectedDate)
  const [now, setNow] = useState(() => new Date())

  // Update clock every minute
  useEffect(() => {
    if (!today) return
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [today])

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2">
      {/* Left: live clock or date */}
      <h1 className="text-lg font-semibold truncate font-mono tracking-tight">
        {today ? formatLiveClock(now) : formatDateOnly(selectedDate)}
      </h1>

      {/* Right: counter pills (icon + number, full label on hover) */}
      <div className="flex items-center gap-1.5 shrink-0">
        <CounterPill
          icon={PlaneLanding}
          value={counters.arriving}
          tooltip={`${counters.arriving} Arriving`}
          colorClasses="bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300"
          iconColor="text-blue-600"
        />
        <CounterPill
          icon={Plane}
          value={counters.onGround}
          tooltip={`${counters.onGround} On Ground`}
          colorClasses="bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300"
          iconColor="text-green-600"
        />
        <CounterPill
          icon={PlaneTakeoff}
          value={counters.departing}
          tooltip={`${counters.departing} Departing`}
          colorClasses="bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300"
          iconColor="text-orange-600"
        />
        <CounterPill
          icon={Building2}
          value={`${counters.occupancyPercent}%`}
          tooltip={`${counters.occupancyPercent}% Occupancy`}
          colorClasses="bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300"
          iconColor="text-violet-600"
        />
      </div>
    </div>
  )
}
